import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { Col, Row, Card, CardBody, CardHeader, Button } from "reactstrap";
import { Fn_AddEditData, Fn_DisplayData, Fn_FillListData } from "../../store/Functions";
import { useDispatch } from "react-redux";
import { API_WEB_URLS } from "../../constants/constAPI";
import { useLocation, useNavigate } from "react-router-dom";
import { Save, ArrowLeft, MapPin, Settings } from "react-feather";
import Breadcrumbs from "../../components/Common/Breadcrumb";

const AddEdit_MemberMaster = () => {
  const API_URL_SAVE = "MemberMaster/0/token";
  const API_URL_EDIT = API_WEB_URLS.MASTER + "/0/token/MemberMaster/Id";
  const API_URL_CITY = `${API_WEB_URLS.MASTER}/0/token/CityMaster/Id/0`;
  const API_URL_STATE = `${API_WEB_URLS.MASTER}/0/token/StateMaster/Id/0`;
  const API_URL_EDUCATION = `${API_WEB_URLS.MASTER}/0/token/EducationMaster/Id/0`;
  const API_URL_USER_TYPE = `${API_WEB_URLS.MASTER}/0/token/UserTypeMaster/Id/0`;
  const API_URL_VEHICLE_TYPE = `${API_WEB_URLS.MASTER}/0/token/VehicleType/Id/0`;
  
  const breadCrumbTitle = "Member Master";
  const breadcrumbItem = "Member Master";

  const [state, setState] = useState({
    id: 0,
    formData: { 
      F_UserTypeMaster: "",
      VehicleRegistrationNo: "",
      F_VehicleType: "",
      VehicleName: "",
      VehicleNo: "",
      WeightCapacity: "",
      Name: "", 
      FatherName: "",
      MotherName: "",
      DateOfBirth: "",
      DateOfJoining: "",
      Age: "",
      F_EducationMaster: "",
      Email: "",
      MobileNo: "",
      AlternateMobileNo: "",
      PANNo: "",
      AadharNo: "",
      DrivingLicence: "",
      Address: "",
      F_StateMaster: "",
      F_CityMaster: "",
      Pincode: "",
      F_Gender: "",
      UserName: "",
      UserPassword: "",
    }
  });

  const [cityList, setCityList] = useState([]);
  const [filteredCityList, setFilteredCityList] = useState([]);
  const [stateList, setStateList] = useState([]);
  const [educationList, setEducationList] = useState([]);
  const [userTypeList, setUserTypeList] = useState([]);
  const [vehicleTypeList, setVehicleTypeList] = useState([]);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const nameRef = useRef(null);

  useEffect(() => {
    const Id = (location.state && location.state.Id) || 0;

    if (Id > 0) {
      setState((prevState) => ({ ...prevState, id: Id }));
      Fn_DisplayData(dispatch, setState, Id, API_URL_EDIT);
    }
  }, [dispatch, location.state]);

  // Fetch all master lists
  useEffect(() => {
    Fn_FillListData(dispatch, setCityList, "gridData", API_URL_CITY);
    Fn_FillListData(dispatch, setStateList, "gridData", API_URL_STATE);
    Fn_FillListData(dispatch, setEducationList, "gridData", API_URL_EDUCATION);
    Fn_FillListData(dispatch, setUserTypeList, "gridData", API_URL_USER_TYPE);
    Fn_FillListData(dispatch, setVehicleTypeList, "gridData", API_URL_VEHICLE_TYPE);
  }, [dispatch]);

  // Auto-focus on Name field when component mounts
  useEffect(() => {
    if (nameRef.current) {
      nameRef.current.focus();
    }
  }, []);

  // Function to handle state change and filter cities
  const handleStateChange = (stateId, setFieldValue) => {
    setFieldValue('F_StateMaster', stateId);
    setFieldValue('F_CityMaster', ''); // Reset city when state changes
    
    if (stateId) {
      // Filter cities based on selected state
      const filtered = cityList.filter(city => city.F_StateMaster == stateId);
      setFilteredCityList(filtered);
    } else {
      setFilteredCityList([]);
    }
  };

  // Initialize filtered cities when editing
  useEffect(() => {
    if (state.formData.F_StateMaster && cityList.length > 0) {
      const filtered = cityList.filter(city => city.F_StateMaster == state.formData.F_StateMaster);
      setFilteredCityList(filtered);
    }
  }, [state.formData.F_StateMaster, cityList]);


  // Validation Schema
  const validationSchema = Yup.object({
    F_UserTypeMaster: Yup.string().required("User type is required"),
    F_VehicleType: Yup.string().when("F_UserTypeMaster", {
      is: (value) => String(value) === "2",
      then: (schema) =>
        schema.required("Vehicle type is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    VehicleRegistrationNo: Yup.string().when("F_UserTypeMaster", {
      is: (value) => String(value) === "2",
      then: (schema) =>
        schema
          .required("Vehicle registration number is required")
          .max(150, "Must be less than 150 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    VehicleName: Yup.string().when("F_UserTypeMaster", {
      is: (value) => String(value) === "2",
      then: (schema) =>
        schema
          .required("Vehicle name is required")
          .max(150, "Must be less than 150 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    VehicleNo: Yup.string().when("F_UserTypeMaster", {
      is: (value) => String(value) === "2",
      then: (schema) =>
        schema
          .required("Vehicle number is required")
          .max(150, "Must be less than 150 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    WeightCapacity: Yup.string().when("F_UserTypeMaster", {
      is: (value) => String(value) === "2",
      then: (schema) =>
        schema
          .required("Weight capacity is required")
          .max(50, "Must be less than 50 characters"),
      otherwise: (schema) => schema.notRequired(),
    }),
    Name: Yup.string()
        .required("Member name is required")
      .min(2, "Member name must be at least 2 characters")
      .max(250, "Member name must be less than 250 characters"),
    FatherName: Yup.string()
      .required("Father name is required")
      .min(2, "Father name must be at least 2 characters")
      .max(250, "Father name must be less than 250 characters"),
    MotherName: Yup.string()
      .required("Mother name is required")
      .min(2, "Mother name must be at least 2 characters")
      .max(250, "Mother name must be less than 250 characters"),
    DateOfBirth: Yup.string()
      .required("Date of birth is required"),
    DateOfJoining: Yup.string()
      .required("Date of joining is required"),
    Age: Yup.number()
      .required("Age is required")
      .min(0, "Age must be 0 or greater")
      .max(150, "Age must be less than 150"),
    F_EducationMaster: Yup.string()
      .required("Education is required"),
    Email: Yup.string()
      .required("Email is required")
      .email("Invalid email address")
      .max(250, "Email must be less than 250 characters"),
    MobileNo: Yup.string()
      .required("Mobile number is required")
      .min(10, "Mobile number must be at least 10 characters")
      .max(250, "Mobile number must be less than 250 characters"),
    AlternateMobileNo: Yup.string()
      .min(10, "Alternate mobile number must be at least 10 characters")
      .max(250, "Alternate mobile number must be less than 250 characters"),
    PANNo: Yup.string()
      .required("PAN number is required")
      .min(10, "PAN number must be at least 10 characters")
      .max(100, "PAN number must be less than 100 characters"),
    AadharNo: Yup.string()
      .required("Aadhar number is required")
      .min(12, "Aadhar number must be at least 12 characters")
      .max(100, "Aadhar number must be less than 100 characters"),
    DrivingLicence: Yup.string()
      .required("Driving licence is required")
      .max(200, "Driving licence must be less than 200 characters"),
    Address: Yup.string()
      .required("Address is required")
      .min(2, "Address must be at least 2 characters")
      .max(450, "Address must be less than 450 characters"),
    F_StateMaster: Yup.string()
      .required("State is required"),
    F_CityMaster: Yup.string()
      .required("City is required"),
    Pincode: Yup.string()
      .required("Pin code is required")
      .min(6, "Pin code must be at least 6 characters")
      .max(50, "Pin code must be less than 50 characters"),
    F_Gender: Yup.string()
      .required("Gender is required"),
    UserName: Yup.string()
      .required("Username is required")
      .max(100, "Username must be less than 100 characters"),
    UserPassword: Yup.string()
      .required("Password is required")
      .max(100, "Password must be less than 100 characters"),
  });

  const handleSubmit = async (values) => {
   const obj = JSON.parse(localStorage.getItem("user"));
       let formData = new FormData();
      formData.append("Name", values.Name || "");
      formData.append("FatherName", values.FatherName || "");
      formData.append("MotherName", values.MotherName || "");
    formData.append("DateOfBirth", values.DateOfBirth || "");
    formData.append("DateOfJoining", values.DateOfJoining || "");
    formData.append("Age", values.Age || "");
    formData.append("F_EducationMaster", values.F_EducationMaster || "");
    formData.append("Email", values.Email || "");
      formData.append("MobileNo", values.MobileNo || "");
    formData.append("AlternateMobileNo", values.AlternateMobileNo || "");
    formData.append("PANNo", values.PANNo || "");
    formData.append("AadharNo", values.AadharNo || "");
    formData.append("DrivingLicence", values.DrivingLicence || "");
      formData.append("Address", values.Address || "");
    formData.append("F_StateMaster", values.F_StateMaster || "");
    formData.append("F_CityMaster", values.F_CityMaster || "");
    formData.append("Pincode", values.Pincode || "");
    formData.append("F_Gender", values.F_Gender || "");
    formData.append("F_UserTypeMaster", values.F_UserTypeMaster || "");
    formData.append("F_VehicleType", values.F_VehicleType || "");
    formData.append("VehicleRegistrationNo", values.VehicleRegistrationNo || "");
    formData.append("VehicleName", values.VehicleName || "");
    formData.append("VehicleNo", values.VehicleNo || "");
    formData.append("WeightCapacity", values.WeightCapacity || "");
    formData.append("UserName", values.UserName || "");
    formData.append("UserPassword", values.UserPassword || "");
    formData.append("UserId", obj === null || obj === undefined ? 0 : obj.uid);
   
       Fn_AddEditData(
         dispatch,
         setState,
         { arguList: { id: state.id, formData } },
         API_URL_SAVE,
         true,
         "memberid",
         navigate,
         "/MemberMaster"
       );
  };

  return (
    <div className="master-form-container" style={{padding: '10px 0'}}>
      <div className="container-fluid px-2">
        <Breadcrumbs title={breadCrumbTitle} breadcrumbItem={breadcrumbItem} />
        
        {/* Form Card */}
        <Row className="justify-content-center">
          <Col lg={10} md={12} sm={12}>
            <Card className="master-form-card master-form-slide-in">
              <CardHeader className="master-form-card-header" style={{padding: '12px 20px'}}>
                <div className="d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center">
                    <div className="form-icon-wrapper me-2">
                      <Settings size={18} color="#6D68CB" />
                    </div>
                    <div>
                      <h5 className="mb-0 fw-bold">Member Master</h5>
                    </div>
                  </div>
                </div>
              </CardHeader>
              
              <CardBody className="master-form-card-body">
                <Formik 
                  initialValues={{
                    F_UserTypeMaster: state.formData.F_UserTypeMaster || "",
                    F_VehicleType: state.formData.F_VehicleType || "",
                    VehicleRegistrationNo: state.formData.VehicleRegistrationNo || "",
                    VehicleName: state.formData.VehicleName || "",
                    VehicleNo: state.formData.VehicleNo || "",
                    WeightCapacity: state.formData.WeightCapacity || "",
                    Name: state.formData.Name || "",
                    FatherName: state.formData.FatherName || "",
                    MotherName: state.formData.MotherName || "",
                    DateOfBirth: state.formData.DateOfBirth || "",
                    DateOfJoining: state.formData.DateOfJoining || "",
                    Age: state.formData.Age || "",
                    F_EducationMaster: state.formData.F_EducationMaster || "",
                    Email: state.formData.Email || "",
                    MobileNo: state.formData.MobileNo || "",
                    AlternateMobileNo: state.formData.AlternateMobileNo || "",
                    PANNo: state.formData.PANNo || "",
                    AadharNo: state.formData.AadharNo || "",
                    DrivingLicence: state.formData.DrivingLicence || "",
                    Address: state.formData.Address || "",
                    F_StateMaster: state.formData.F_StateMaster || "",
                    F_CityMaster: state.formData.F_CityMaster || "",
                    Pincode: state.formData.Pincode || "",
                    F_Gender: state.formData.F_Gender || "",
                    UserName: state.formData.UserName || "",
                    UserPassword: state.formData.UserPassword || "",
                  }} 
                  validationSchema={validationSchema} 
                  onSubmit={handleSubmit}
                  enableReinitialize={true}
                >
                  {({ isSubmitting, errors, touched, setFieldValue, values }) => (
                    <Form>
                      <div className="master-form-grid-2">
                        <div className="form-group mb-3">
                          <label htmlFor="F_UserTypeMaster" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            User Type <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_UserTypeMaster && touched.F_UserTypeMaster ? 'is-invalid' : ''}`}
                              name="F_UserTypeMaster"
                            >
                              <option value="">Select User Type</option>
                              {userTypeList && userTypeList.map((userType) => (
                                <option key={userType.Id} value={userType.Id}>
                                  {userType.Name}
                                </option>
                              ))}
                            </Field>
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="F_UserTypeMaster" component="div" className="master-form-error" />
                        </div>

                        {String(values.F_UserTypeMaster) === "2" && (
                          <>
                            <div className="form-group mb-3">
                              <label htmlFor="F_VehicleType" className="master-form-label">
                                <MapPin size={16} className="me-2" />
                                Vehicle Type <span className="required">*</span>
                              </label>
                              <div className="input-wrapper">
                                <Field
                                  as="select"
                                  className={`master-form-input ${errors.F_VehicleType && touched.F_VehicleType ? 'is-invalid' : ''}`}
                                  name="F_VehicleType"
                                >
                                  <option value="">Select Vehicle Type</option>
                                  {vehicleTypeList && vehicleTypeList.map((type) => {
                                    const hasRange = type.RangeFrom !== undefined && type.RangeFrom !== null && type.RangeTo !== undefined && type.RangeTo !== null;
                                    return (
                                      <option key={type.Id} value={type.Id}>
                                        {type.Name}
                                        {hasRange ? ` (${type.RangeFrom} - ${type.RangeTo})` : ""}
                                      </option>
                                    );
                                  })}
                                </Field>
                                <div className="input-icon">
                                  <MapPin size={16} color="#6c757d" />
                                </div>
                              </div>
                              <ErrorMessage name="F_VehicleType" component="div" className="master-form-error" />
                            </div>

                            <div className="form-group mb-3">
                              <label htmlFor="VehicleRegistrationNo" className="master-form-label">
                                <MapPin size={16} className="me-2" />
                                Vehicle Registration No <span className="required">*</span>
                              </label>
                              <div className="input-wrapper">
                                <Field
                                  className={`master-form-input ${errors.VehicleRegistrationNo && touched.VehicleRegistrationNo ? 'is-invalid' : ''}`}
                                  type="text"
                                  name="VehicleRegistrationNo"
                                  placeholder="Enter vehicle registration number"
                                />
                                <div className="input-icon">
                                  <MapPin size={16} color="#6c757d" />
                                </div>
                              </div>
                              <ErrorMessage name="VehicleRegistrationNo" component="div" className="master-form-error" />
                            </div>

                            <div className="form-group mb-3">
                              <label htmlFor="VehicleName" className="master-form-label">
                                <MapPin size={16} className="me-2" />
                                Vehicle Name <span className="required">*</span>
                              </label>
                              <div className="input-wrapper">
                                <Field
                                  className={`master-form-input ${errors.VehicleName && touched.VehicleName ? 'is-invalid' : ''}`}
                                  type="text"
                                  name="VehicleName"
                                  placeholder="Enter vehicle name"
                                />
                                <div className="input-icon">
                                  <MapPin size={16} color="#6c757d" />
                                </div>
                              </div>
                              <ErrorMessage name="VehicleName" component="div" className="master-form-error" />
                            </div>

                            <div className="form-group mb-3">
                              <label htmlFor="VehicleNo" className="master-form-label">
                                <MapPin size={16} className="me-2" />
                                Vehicle No <span className="required">*</span>
                              </label>
                              <div className="input-wrapper">
                                <Field
                                  className={`master-form-input ${errors.VehicleNo && touched.VehicleNo ? 'is-invalid' : ''}`}
                                  type="text"
                                  name="VehicleNo"
                                  placeholder="Enter vehicle number"
                                />
                                <div className="input-icon">
                                  <MapPin size={16} color="#6c757d" />
                                </div>
                              </div>
                              <ErrorMessage name="VehicleNo" component="div" className="master-form-error" />
                            </div>

                            <div className="form-group mb-3">
                              <label htmlFor="WeightCapacity" className="master-form-label">
                                <MapPin size={16} className="me-2" />
                                Weight Capacity (KG) <span className="required">*</span>
                              </label>
                              <div className="input-wrapper">
                                <Field
                                  className={`master-form-input ${errors.WeightCapacity && touched.WeightCapacity ? 'is-invalid' : ''}`}
                                  type="number"
                                  name="WeightCapacity"
                                  placeholder="Enter weight capacity"
                                  min="0"
                                />
                                <div className="input-icon">
                                  <MapPin size={16} color="#6c757d" />
                                </div>
                              </div>
                              <ErrorMessage name="WeightCapacity" component="div" className="master-form-error" />
                            </div>
                          </>
                        )}
                        <div className="form-group mb-3">
                          <label htmlFor="Name" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Member Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              innerRef={nameRef}
                              className={`master-form-input ${errors.Name && touched.Name ? 'is-invalid' : ''}`}
                              type="text"
                              name="Name"
                              placeholder="Enter member name"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Name" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="FatherName" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Father Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.FatherName && touched.FatherName ? 'is-invalid' : ''}`}
                              type="text"
                              name="FatherName"
                              placeholder="Enter father name"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="FatherName" component="div" className="master-form-error" />
                          </div>

                        <div className="form-group mb-3">
                          <label htmlFor="MotherName" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Mother Name <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.MotherName && touched.MotherName ? 'is-invalid' : ''}`}
                              type="text"
                              name="MotherName"
                              placeholder="Enter mother name"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="MotherName" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="DateOfBirth" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Date of Birth <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.DateOfBirth && touched.DateOfBirth ? 'is-invalid' : ''}`}
                              type="date"
                              name="DateOfBirth"
                            />
                          </div>
                          <ErrorMessage name="DateOfBirth" component="div" className="master-form-error" />
                            </div>

                        <div className="form-group mb-3">
                          <label htmlFor="DateOfJoining" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Date of Joining <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.DateOfJoining && touched.DateOfJoining ? 'is-invalid' : ''}`}
                              type="date"
                              name="DateOfJoining"
                            />
                          </div>
                          <ErrorMessage name="DateOfJoining" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Age" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Age <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Age && touched.Age ? 'is-invalid' : ''}`}
                              type="number"
                              name="Age"
                              placeholder="Enter age"
                              min="0"
                              max="150"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Age" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Enter the member's age in years
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_EducationMaster" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Education <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_EducationMaster && touched.F_EducationMaster ? 'is-invalid' : ''}`}
                              name="F_EducationMaster"
                            >
                              <option value="">Select Education</option>
                              {educationList && educationList.map((education) => (
                                <option key={education.Id} value={education.Id}>
                                  {education.Name}
                                </option>
                              ))}
                            </Field>
                            </div>
                          <ErrorMessage name="F_EducationMaster" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Email" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Email <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Email && touched.Email ? 'is-invalid' : ''}`}
                              type="email"
                              name="Email"
                              placeholder="Enter email address"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Email" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="MobileNo" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Mobile No <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.MobileNo && touched.MobileNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="MobileNo"
                              placeholder="Enter mobile number"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="MobileNo" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="AlternateMobileNo" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Alternate Mobile No <span className="text-muted">(Optional)</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.AlternateMobileNo && touched.AlternateMobileNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="AlternateMobileNo"
                              placeholder="Enter alternate mobile number"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                          </div>
                        </div>
                          <ErrorMessage name="AlternateMobileNo" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="PANNo" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            PAN No <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.PANNo && touched.PANNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="PANNo"
                              placeholder="Enter PAN number"
                              style={{ textTransform: 'uppercase' }}
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="PANNo" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="AadharNo" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Aadhar No <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.AadharNo && touched.AadharNo ? 'is-invalid' : ''}`}
                              type="text"
                              name="AadharNo"
                              placeholder="Enter aadhar number"
                              maxLength="12"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="AadharNo" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="DrivingLicence" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Driving Licence <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.DrivingLicence && touched.DrivingLicence ? 'is-invalid' : ''}`}
                              type="text"
                              name="DrivingLicence"
                              placeholder="Enter driving licence number"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="DrivingLicence" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Address" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Address <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Address && touched.Address ? 'is-invalid' : ''}`}
                              as="textarea"
                              name="Address"
                              placeholder="Enter address"
                              rows="2"
                            />
                          </div>
                          <ErrorMessage name="Address" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_StateMaster" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            State <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_StateMaster && touched.F_StateMaster ? 'is-invalid' : ''}`}
                              name="F_StateMaster"
                              onChange={(e) => handleStateChange(e.target.value, setFieldValue)}
                            >
                              <option value="">Select State</option>
                              {stateList && stateList.map((state) => (
                                <option key={state.Id} value={state.Id}>
                                  {state.Name}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_StateMaster" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Select state first to load cities
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_CityMaster" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            City <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_CityMaster && touched.F_CityMaster ? 'is-invalid' : ''}`}
                              name="F_CityMaster"
                            >
                              <option value="">Select City</option>
                              {filteredCityList && filteredCityList.map((city) => (
                                <option key={city.Id} value={city.Id}>
                                  {city.Name}
                                </option>
                              ))}
                            </Field>
                          </div>
                          <ErrorMessage name="F_CityMaster" component="div" className="master-form-error" />
                          <div className="form-help-text" style={{fontSize: '0.75rem', marginTop: '2px'}}>
                            <i className="fas fa-lightbulb me-1"></i>
                            Cities based on selected state
                          </div>
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="Pincode" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Pin Code <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.Pincode && touched.Pincode ? 'is-invalid' : ''}`}
                              type="text"
                              name="Pincode"
                              placeholder="Enter pin code"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="Pincode" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="F_Gender" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Gender <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              as="select"
                              className={`master-form-input ${errors.F_Gender && touched.F_Gender ? 'is-invalid' : ''}`}
                              name="F_Gender"
                            >
                              <option value="">Select Gender</option>
                              <option value="Male">Male</option>
                              <option value="Female">Female</option>
                              <option value="Other">Other</option>
                            </Field>
                          </div>
                          <ErrorMessage name="F_Gender" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="UserName" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Username <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.UserName && touched.UserName ? 'is-invalid' : ''}`}
                              type="text"
                              name="UserName"
                              placeholder="Enter username"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="UserName" component="div" className="master-form-error" />
                        </div>

                        <div className="form-group mb-3">
                          <label htmlFor="UserPassword" className="master-form-label">
                            <MapPin size={16} className="me-2" />
                            Password <span className="required">*</span>
                          </label>
                          <div className="input-wrapper">
                            <Field
                              className={`master-form-input ${errors.UserPassword && touched.UserPassword ? 'is-invalid' : ''}`}
                              type="password"
                              name="UserPassword"
                              placeholder="Enter password"
                            />
                            <div className="input-icon">
                              <MapPin size={16} color="#6c757d" />
                            </div>
                          </div>
                          <ErrorMessage name="UserPassword" component="div" className="master-form-error" />
                        </div>
                      </div>

                      <div className="master-form-actions mt-3">
                        <div className="d-flex gap-2 justify-content-center">
                          <Button
                            type="button"
                            className="master-form-cancel-btn"
                            onClick={() => navigate("/MemberMaster")}
                            variant="outline-secondary"
                            size="md"
                          >
                            <ArrowLeft size={16} className="me-1" />
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            className="master-form-submit-btn"
                            disabled={isSubmitting}
                            size="md"
                          >
                            {isSubmitting ? (
                              <div className="master-form-loading me-1" />
                            ) : (
                              <Save size={16} className="me-1" />
                            )}
                            {state.id > 0 ? 'Update Member' : 'Save Member'}
                          </Button>
                        </div>
                      </div>
                    </Form>
                  )}
                </Formik>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  );
};

export default AddEdit_MemberMaster;